import { useState, useEffect, useRef } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { Trash } from "iconsax-reactjs";
import RadioButton from "../../components/partials/message/RadioButton";
import CheckBoxButton from "../../components/partials/message/CheckBoxButton";
import ModalWithImageBackground from "../../components/partials/message/ModalWithImageBackground";
import ModalWithImageEmailBackground from "../../components/partials/message/ModalWithImageEmailBackground";
import ConfirmModal from "../../components/partials/message/ConfirmModal";
import type { UserSearchParams, User } from "../../services/webServices/user/types";
import type { Event, SearchEventParams } from "../../services/webServices/event/types";
import type { Course, SearchCourseParams } from "../../services/webServices/course/types";
import type { CategoryListItem, GetCategorySearchParams } from "../../services/webServices/category/types";
import type { SendRegistrantsMessageRequest } from "../../services/webServices/message/types";
import { getUsers } from "../../services/webServices/user/get";
import { getEvents } from "../../services/webServices/event/get";
import { getCourses } from "../../services/webServices/course/get";
import { getCategorySearch } from "../../services/webServices/category/get";
import { sendRegistrantsMessage } from "../../services/webServices/message/post";

type ReceiverType = "personal" | "event" | "course" | "category";

interface SelectedItem {
  id: string | number;
  type: ReceiverType;
  title: string;
  studentsCount: number;
  display: string;
}

type SendingMethod = "sms" | "email";
type SendingPerson = "students" | "teachers";

export default function SendMessage() {

  const [message, setMessage] = useState("");
  const [selectedReceiver, setSelectedReceiver] = useState<ReceiverType | "">("");
  const [inputValue, setInputValue] = useState("");
  const [isOpenList, setIsOpenList] = useState<boolean>(false);
  const [selectedSendingMethod, setSelectedSendingMethod] = useState<SendingMethod>("sms");
  const [selectedSendingPerson, setSelectedSendingPerson] = useState<SendingPerson[]>([]);
  const [emailTitle, setEmailTitle] = useState("");
  const [emailContent, setEmailContent] = useState("");

  const [searchedUsers, setSearchedUsers] = useState<User[]>([]);
  const [searchedEvents, setSearchedEvents] = useState<Event[]>([]);
  const [searchedCourses, setSearchedCourses] = useState<Course[]>([]);
  const [searchedCategory, setSearchedCategory] = useState<CategoryListItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);

  const [userOffset, setUserOffset] = useState(0);
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isClearFormModalOpen, setIsClearFormModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  const listRef = useRef<HTMLDivElement | null>(null);

  const clearSearchResults = () => {
    setSearchedUsers([]);
    setSearchedEvents([]);
    setSearchedCourses([]);
    setSearchedCategory([]);
    setIsOpenList(false);
    setUserOffset(0);
    setHasMoreUsers(true);
    setIsLoadingUsers(false);
  };

  const handleRadioChange = (value: ReceiverType) => {
    setSelectedReceiver(value);
    setInputValue("");
    clearSearchResults();
    setUserOffset(0);
    setHasMoreUsers(true);
    setIsLoadingUsers(false);
  };

  const getSuggestedUser = async (fullName: string, isLoadMore = false) => {
    if (isLoadingUsers) return;

    const currentOffset = isLoadMore ? userOffset : 0;
    const params: UserSearchParams = {
      offset: currentOffset,
      limit: 8,
      search: fullName,
      ordering: "-created_at",
    };

    setIsLoadingUsers(true);

    const response = await getUsers({ params });
    if (response?.length) {
      if (isLoadMore) {
        setSearchedUsers((prev) => [...prev, ...response]);
      } else {
        setSearchedUsers(response);
      }
      const newOffset = currentOffset + response.length;
      setUserOffset(newOffset);
      setHasMoreUsers(response.length === 8);
      setIsOpenList(true);
    } else {
      if (!isLoadMore) {
        setIsOpenList(false);
      }
      setHasMoreUsers(false);
    }

    setIsLoadingUsers(false);
  };

  const loadMoreUsers = () => {
    if (
      selectedReceiver === "personal" &&
      inputValue &&
      hasMoreUsers &&
      !isLoadingUsers
    ) {
      getSuggestedUser(inputValue, true);
    }
  };

  const getSuggestedEvent = async (eventTitle: string) => {
    const params: SearchEventParams = {
      offset: 0,
      limit: 10,
      event_status: [1, 2, 4],
      search: eventTitle,
      publish_status: 3,
      ordering: "-created_at",
    };

    const response = await getEvents({ params });
    if (response?.length) {
      setSearchedEvents(response);
      setIsOpenList(true);
    } else {
      setIsOpenList(false);
    }
  };

  const getSuggestedCourse = async (courseTitle: string) => {
    const params: SearchCourseParams = {
      offset: 0,
      limit: 10,
      search: courseTitle,
      publish_status: 5,
      ordering: "-created_at",
    };

    const response = await getCourses({ params });
    if (response?.length) {
      setSearchedCourses(response);
      setIsOpenList(true);
    } else {
      setIsOpenList(false);
    }
  };

  const getSuggestedCategory = async (categoryTitle: string) => {
    const params: GetCategorySearchParams["params"] = {
      offset: 0,
      limit: 10,
      search: categoryTitle,
      depth: 1,
    };

    const response = await getCategorySearch({ params });

    if (response?.length) {
      setSearchedCategory(response);
      setIsOpenList(true);
    } else {
      setIsOpenList(false);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (listRef.current && !listRef.current.contains(event.target as Node)) {
      setIsOpenList(false);
    }
  };

  const addSelectedItem = (item: SelectedItem) => {
    setSelectedItems((prev) => {
      if (item.type === "category") {
        return [...prev.filter((i) => i.type !== "category"), item];
      }
      if (prev.some((i) => i.type === item.type && i.id === item.id))
        return prev;

      return [...prev, item];
    });
  };

  const handleSelectUser = (user: User) => {
    setInputValue(user.username);
    setIsOpenList(false);
    addSelectedItem({
      id: user.id,
      type: "personal",
      title: user.full_name,
      studentsCount: 1,
      display: user.full_name,
    });
    setInputValue("");
  };

  const handleSelectEvent = (event: Event) => {
    setInputValue(event.title);
    setIsOpenList(false);
    addSelectedItem({
      id: event.id,
      type: "event",
      title: event.title,
      studentsCount: event.students_count || 0,
      display: event.title,
    });
    setInputValue("");
  };

  const handleSelectCourse = (course: Course) => {
    setInputValue(course.title);
    setIsOpenList(false);
    addSelectedItem({
      id: course.id,
      type: "course",
      title: course.title,
      studentsCount: course.students_count || 0,
      display: course.title,
    });
    setInputValue("");
  };
  const handleSelectCategory = (category: CategoryListItem) => {
    setInputValue(category.title);
    setIsOpenList(false);
    addSelectedItem({
      id: category.id,
      type: "category",
      title: category.title,
      studentsCount: 0,
      display: category.title,
    });
    setInputValue("");
  };

  const handleRemoveItem = (item: SelectedItem) => {
    setSelectedItems((prev) =>
      prev.filter((i) => !(i.type === item.type && i.id === item.id))
    );
  };

  const handleClearForm = () => {
    setMessage("");
    setEmailTitle("");
    setEmailContent("");
    setSelectedItems([]);
    setSelectedReceiver("");
    setInputValue("");
    clearSearchResults();
    setIsClearFormModalOpen(false);
  };

  const handleSendNewMessage = () => {
    setMessage("");
    setEmailTitle("");
    setEmailContent("");
    setSelectedItems([]);
    setSelectedReceiver("");
    setInputValue("");
    clearSearchResults();
    setIsSuccessModalOpen(false);
  };

  const totalStudents = selectedItems.reduce(
    (sum, item) => sum + (item.studentsCount || 0),
    0
  );

  const handleSendMessage = async () => {
    if (selectedItems.length === 0) return;

    if (selectedSendingMethod === "sms" && !message.trim()) {
      return;
    }

    if (
      selectedSendingMethod === "email" &&
      (!emailTitle.trim() || !emailContent.trim())
    ) {
      return;
    }

    const students_of_courses_id: string[] = [];
    const students_of_events_id: string[] = [];
    const users_id: string[] = [];
    let students_of_category_id: number | null = null;
    let teachers_of_category_id: number | null = null;

    selectedItems.forEach((item) => {
      switch (item.type) {
        case "course":
          students_of_courses_id.push(item.id.toString());
          break;
        case "event":
          students_of_events_id.push(item.id.toString());
          break;
        case "personal":
          users_id.push(item.id.toString());
          break;
        case "category":
          if (selectedSendingPerson.includes("students")) {
            students_of_category_id = Number(item.id);
          }
          if (selectedSendingPerson.includes("teachers")) {
            teachers_of_category_id = Number(item.id);
          }
          break;
      }
    });

    const requestData: SendRegistrantsMessageRequest = {
      students_of_courses_id: students_of_courses_id.length > 0 ? students_of_courses_id : null,
      students_of_events_id: students_of_events_id.length > 0 ? students_of_events_id : null,
      users_id: users_id.length > 0 ? users_id : null,
      students_of_category_id: students_of_category_id || null,
      teachers_of_category_id: teachers_of_category_id || null,
      subject: selectedSendingMethod === "email" ? emailTitle : null,
      message: selectedSendingMethod === "sms" ? message : emailContent,
      send_method: selectedSendingMethod === "sms" ? 1 : 2,
    };

    const isSuccess = await sendRegistrantsMessage(requestData);

    if (isSuccess) {
      setIsSuccessModalOpen(true);
    } else {
      setIsErrorModalOpen(true);
    }
  };

  const getInputPlaceholder = (): string => {
    const placeholders = {
      course: "عنوان دوره را وارد کنید",
      event: "عنوان رویداد را وارد کنید",
      category: "عنوان دسته بندی را وارد کنید",
      personal: "نام کاربر را وارد کنید",
    };
    return placeholders[selectedReceiver as ReceiverType] || "";
  };

  const getInputLabel = (): string => {
    const labels = {
      course: "عنوان دوره:",
      event: "عنوان رویداد:",
      category: "افراد دسته بندی خاص:",
      personal: "نام کاربر:",
    };
    return labels[selectedReceiver as ReceiverType] || "";
  };

  useEffect(() => {
    if (!inputValue) {
      setIsOpenList(false);
      if (selectedReceiver === "personal") {
        setUserOffset(0);
        setHasMoreUsers(true);
      }
      return;
    }

    if (selectedReceiver === "personal") {
      setUserOffset(0);
      setHasMoreUsers(true);
    }

    const searchFunctions = {
      personal: () => getSuggestedUser(inputValue),
      event: () => getSuggestedEvent(inputValue),
      course: () => getSuggestedCourse(inputValue),
      category: () => getSuggestedCategory(inputValue),
    };

    const searchFunction = searchFunctions[selectedReceiver as ReceiverType];
    if (searchFunction) {
      searchFunction();
    }
  }, [inputValue, selectedReceiver]);

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const renderUserItem = (user: User, index: number) => (
    <div
      key={index}
      className="w-full flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4 p-4"
    >
      <div className="flex items-center gap-2 w-full sm:flex-1">
        <div className="profile-pic-container3">
          <img
            src={
              user?.avatar
                ? `${process.env.REACT_APP_File_URL}${user.avatar}`
                : "/images/avatar-alt.jpg"
            }
            alt="Profile Picture"
            className="profile-pic"
          />
        </div>
        <h6 className="text-gray-500 text-sm" dir="ltr">
          @{user?.username} | {user?.full_name}
        </h6>
      </div>

      <button
        color="primary"
        onClick={() => handleSelectUser(user)}
        className="w-full sm:w-auto px-6 py-2 rounded-xl transition-colors duration-300 text-primaryVar border border-primaryVar"
      >
        افزودن
      </button>
    </div>
  );

  const renderEventItem = (event: Event, index: number) => (
    <div
      key={index}
      className="w-full flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4 p-4"
    >
      <div className="flex items-center gap-2 w-full sm:flex-1">
        <div className="w-11 h-9 ">
          <img
            src={
              event?.cover
                ? `${process.env.REACT_APP_File_URL}${event.cover}`
                : "/images/course-alt.jpg"
            }
            alt="Event"
            className="profile-pic"
          />
        </div>
        <div className="flex flex-row gap-3">
          <h6 className="text-gray-500 text-sm font-semibold">
            {event?.title}
          </h6>

          <h6 className="text-gray-500 text-sm font-semibold">|</h6>
          <h6 className="text-gray-500 text-sm  ">
            {event?.students_count} نفر
          </h6>
          <h6 className="text-gray-500 text-sm font-semibold">|</h6>
          <h6 className="text-gray-500 text-sm  ">{event?.user.full_name}</h6>
        </div>
      </div>

      <button
        color="primary"
        onClick={() => handleSelectEvent(event)}
        className="w-full sm:w-auto px-6 py-2 rounded-xl transition-colors duration-300 text-primaryVar border border-primaryVar"
      >
        افزودن
      </button>
    </div>
  );

  const renderCourseItem = (course: Course, index: number) => (
    <div
      key={index}
      className="w-full flex border-b flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4 p-4"
    >
      <div className="flex items-center gap-2 w-full sm:flex-1">
        <div className="w-11 h-9 ">
          <img
            src={
              course?.image
                ? `${process.env.REACT_APP_File_URL}${course?.image}`
                : "/images/course-alt.jpg"
            }
            alt="Event Poster"
            className="profile-pic"
          />
        </div>
        <div className="flex flex-row gap-3">
          <h6 className="text-gray-500 text-sm font-semibold">
            {course?.title}
          </h6>
          <h6 className="text-gray-500 text-sm font-semibold">|</h6>
          <h6 className="text-gray-500 text-sm  ">
            {course?.students_count} نفر
          </h6>
          <h6 className="text-gray-500 text-sm font-semibold">|</h6>
          <h6 className="text-gray-500 text-sm  ">{course?.user.full_name}</h6>
        </div>
      </div>

      <button
        color="primary"
        onClick={() => handleSelectCourse(course)}
        className="w-full sm:w-auto px-6 py-2 rounded-xl transition-colors duration-300 text-primaryVar border border-primaryVar"
      >
        افزودن
      </button>
    </div>
  );

  const renderCategoryItem = (category: CategoryListItem, index: number) => (
    <div
      key={index}
      className="w-full flex border-b flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4 p-4"
    >
      <div className="flex items-center gap-2 w-full sm:flex-1">
        <div className="flex flex-row gap-3">
          <h6 className="text-gray-500 text-sm font-semibold">
            {category?.title}
          </h6>
          <h6 className="text-gray-500 text-sm font-semibold">|</h6>
          <h6 className="text-gray-500 text-sm  ">{category?.breadcrump}</h6>
        </div>
      </div>
      <button
        color="primary"
        onClick={() => handleSelectCategory(category)}
        className="w-full sm:w-auto px-6 py-2 rounded-xl transition-colors duration-300 text-primaryVar border border-primaryVar"
      >
        افزودن
      </button>
    </div>
  );

  const receiverOptions = [
    "افراد شرکت کننده رویداد",
    "افراد شرکت کننده دوره",
    "افراد دسته بندی خاص",
    "شخصی",
  ];
  const receiverMap: { [key: string]: string } = {
    شخصی: "personal",
    "افراد شرکت کننده رویداد": "event",
    "افراد شرکت کننده دوره": "course",
    "افراد دسته بندی خاص": "category",
  };
  const receiverMapReverse: { [key: string]: string } = {
    personal: "شخصی",
    event: "افراد شرکت کننده رویداد",
    course: "افراد شرکت کننده دوره",
    category: "افراد دسته بندی خاص",
  };
  const sendingPersonMap: { [key: string]: SendingPerson } = {
    "دانشجویان این دسته بندی": "students",
    "برگزارکنندگان دوره و رویداد این دسته بندی": "teachers",
  };
  const sendingPersonMapReverse: { [key: string]: string } = {
    students: "دانشجویان این دسته بندی",
    teachers: "برگزارکنندگان دوره و رویداد این دسته بندی",
  };

  return (
    <div
      className="w-full flex flex-col  p-4 border rounded-2xl card-shadow bg-white min-h-screen mb-3"
      dir="rtl"
    >
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <label>
            <div className="inline-flex text-base font-semibold items-center gap-2">
              ارسال پیام جدید
            </div>
          </label>

          {selectedItems.length > 0 && (
            <button
              className=" rounded-xl  text-gray-500  duration-300 text-sm border border-primaryVar"
              onClick={() => setIsClearFormModalOpen(true)}
            >
              پاک کردن فرم
            </button>
          )}
        </div>

        <div className="rounded-lg flex flex-col gap-2 mt-2">
          <span className="font-medium text-sm text-gray-700 mt-6 mb-2">
            انتخاب گیرنده(های) پیام:
          </span>

          <RadioButton
            className="flex-row gap-10"
            options={receiverOptions}
            value={receiverMapReverse[selectedReceiver as ReceiverType] || ""}
            onChange={(option) =>
              handleRadioChange(receiverMap[option] as ReceiverType)
            }
          />

          {selectedReceiver && (
            <div className="mt-4 relative">
              <label className="block text-sm font-normal text-gray-600 mt-1 mb-2">
                {getInputLabel()}
              </label>

              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={getInputPlaceholder()}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                dir="rtl"
              />

              {inputValue && isOpenList && (
                <div
                  ref={listRef}
                  className="absolute border bg-white flex flex-col top-full mt-1 w-full card-shadow rounded-2xl z-50"
                >
                  {/* User Suggestions with Infinite Scroll */}
                  {selectedReceiver === "personal" && (
                    <div
                      className="max-h-96 overflow-y-auto"
                      id="scrollableDiv"
                    >
                      <InfiniteScroll
                        dataLength={searchedUsers.length}
                        next={loadMoreUsers}
                        hasMore={hasMoreUsers}
                        loader={
                          <div className="text-center py-4">
                            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primaryVar"></div>
                            <p className="text-sm text-gray-500 mt-2">
                              در حال بارگذاری...
                            </p>
                          </div>
                        }
                        endMessage={
                          <div className="text-center py-4">
                            <p className="text-sm text-gray-500">
                              تمام کاربران نمایش داده شد
                            </p>
                          </div>
                        }
                        scrollableTarget="scrollableDiv"
                        scrollThreshold={0.8}
                      >
                        {searchedUsers.map((user, index) =>
                          renderUserItem(user, index)
                        )}
                      </InfiniteScroll>
                    </div>
                  )}

                  {searchedEvents.map((event, index) =>
                    renderEventItem(event, index)
                  )}
                  {searchedCourses.map((course, index) =>
                    renderCourseItem(course, index)
                  )}
                  {searchedCategory.map((category, index) =>
                    renderCategoryItem(category, index)
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {selectedItems.length > 0 && (
        <div className="mt-8 border rounded-lg">
          <table className="w-full border rounded-lg text-center overflow-hidden">
            <thead className="bg-primaryVar-50 rounded-t-lg text-primaryVar-700 text-sm">
              <tr>
                <th className="p-4 w-1 text-start">عنوان</th>
                <th className="py-2  text-center">تعداد دانشجو</th>
                <th className="py-2">عملیات</th>
              </tr>
            </thead>
            <tbody>
              {selectedItems.map((item, idx) => (
                <tr
                  key={item.type + "-" + item.id}
                  className="border-b border-gray-50"
                >
                  <td className="p-2 w-1/3 text-start text font-normal">
                    {item.display}
                  </td>
                  <td className="py-2 text-center">{item.studentsCount} نفر</td>
                  <td className="py-2 text-center">
                    <button
                      onClick={() => handleRemoveItem(item)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash
                        variant="Outline"
                        size="20"
                        color="#1E1E1E"
                      />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-center py-2 text-xs text-gray-500 my-1 mr-28">
            ارسال پیام برای {totalStudents} نفر
          </div>
        </div>
      )}

      {selectedItems.length > 0 && (
        <div className="mt-6 rounded-lg flex flex-col gap-2">
          <span className="font-medium text-sm text-gray-800 mb-3">
            انتخاب روش ارسال:
          </span>

          <RadioButton
            className="flex-col "
            options={["ارسال با پیامک", "ارسال با ایمیل"]}
            value={
              selectedSendingMethod === "sms"
                ? "ارسال با پیامک"
                : "ارسال با ایمیل"
            }
            onChange={(option) => {
              setSelectedSendingMethod(
                option === "ارسال با پیامک" ? "sms" : "email"
              );
            }}
          />
        </div>
      )}
      {selectedItems.length > 0 && selectedReceiver === "category" && (
        <div className="mt-6 rounded-lg flex flex-col gap-2">
          <span className="font-medium text-sm text-gray-800 mb-3">
            ارسال پیام به:
          </span>
          <CheckBoxButton
            className="flex-col"
            options={[
              "دانشجویان این دسته بندی",
              "برگزارکنندگان دوره و رویداد این دسته بندی",
            ]}
            value={selectedSendingPerson.map((p) => sendingPersonMapReverse[p])}
            onChange={(selected) => {
              const mapped = selected.map((s) => sendingPersonMap[s]);
              setSelectedSendingPerson(mapped);
            }}
          />
        </div>
      )}

      {selectedItems.length > 0 && selectedSendingMethod === "sms" && (
        <div className="mt-6 rounded-lg flex flex-col gap-2">
          <label className="block text-sm w-1/2 font-normal text-gray-500 mt-1  ">
            متن پیامک:
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="متن پیام مورد نظر را وارد کنید یا از قالب متن‌های آماده انتخاب کنید."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            style={{ height: "177px" }}
            dir="rtl"
          />
          <div className="flex gap-3 items-center text-xs text-gray-500">
            <span>900/{message.length} </span>
            <span className="text-infoVar text-xs font-normal">
              {" "}
              این پیام {Math.ceil(message.length / 67)} پیامک خواهد بود
            </span>
          </div>
        </div>
      )}

      {selectedItems.length > 0 && selectedSendingMethod === "email" && (
        <div className="mt-6 rounded-lg flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <label className="block text-sm font-normal text-gray-500 mt-1  ">
              عنوان ایمیل:
            </label>
            <input
              type="text"
              value={emailTitle}
              onChange={(e) => setEmailTitle(e.target.value)}
              placeholder="عنوان ایمیل را وارد کنید"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              dir="rtl"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="block text-sm font-normal text-gray-500 mt-1  ">
              متن ایمیل:
            </label>
            <textarea
              value={emailContent}
              onChange={(e) => setEmailContent(e.target.value)}
              placeholder="متن ایمیل مورد نظر را وارد کنید یا از قالب متن‌های آماده انتخاب کنید."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              style={{ height: "177px" }}
              dir="rtl"
            />
          </div>
        </div>
      )}

      {selectedItems.length > 0 &&
        (selectedSendingMethod === "sms" ||
          selectedSendingMethod === "email") && (
          <div className="mt-6 gap-2 flex justify-end">
            <button
              color="primary"
              className="px-8 py-3 disabled:cursor-not-allowed rounded-xl transition-colors duration-300 bg-primaryVar text-white hover:bg-primaryVar/80 disabled:bg-primaryVar/50"
              onClick={() => setIsPreviewModalOpen(true)}
              disabled={
                (selectedSendingMethod === "sms" && !message.trim()) ||
                (selectedSendingMethod === "email" &&
                  (!emailTitle.trim() || !emailContent.trim()))
              }
            >
              مشاهده پیش نمایش و ارسال{" "}
            </button>
          </div>
        )}

      <ConfirmModal
        isOpen={isSuccessModalOpen}
        onClose={() => setIsSuccessModalOpen(false)}
        type="success"
        title="پیام شما با موفقیت ارسال شد!"
        message="پیام شما با موفقیت به تمام گیرندگان انتخابی ارسال گردید."
        primaryAction={{
          text: "ارسال پیام جدید",
          onClick: handleSendNewMessage,
          variant: "primary",
        }}
        secondaryAction={{
          text: "بستن",
          onClick: () => setIsSuccessModalOpen(false),
        }}
      />

      <ConfirmModal
        isOpen={isErrorModalOpen}
        onClose={() => setIsErrorModalOpen(false)}
        type="error"
        title="خطا در ارسال پیام!"
        message="متأسفانه ارسال پیام شما موفق نبود. لطفاً دقایقی دیگر مجدداً تلاش کنید یا با پشتیبانی تماس بگیرید."
        primaryAction={{
          text: "تلاش مجدد",
          onClick: () => setIsErrorModalOpen(false),
          variant: "primary",
        }}
        secondaryAction={{
          text: "بستن",
          onClick: () => setIsErrorModalOpen(false),
        }}
      />

      <ConfirmModal
        isOpen={isClearFormModalOpen}
        onClose={() => setIsClearFormModalOpen(false)}
        type="confirmation"
        title="تأیید پاک کردن فرم"
        message="آیا مطمئن هستید که می‌خواهید تمام اطلاعات وارد شده در فرم را پاک کنید؟"
        primaryAction={{
          text: "بله، پاک کن",
          onClick: handleClearForm,
          variant: "secondary",
        }}
        secondaryAction={{
          text: "انصراف",
          onClick: () => setIsClearFormModalOpen(false),
        }}
      />

      {selectedSendingMethod === "sms" ? (
        <ModalWithImageBackground
          open={isPreviewModalOpen}
          onClose={() => setIsPreviewModalOpen(false)}
          text={
            <div className="text-right whitespace-pre-line break-words">
              {message}
            </div>
          }
          buttons={
            <>
              <button
                onClick={() => setIsPreviewModalOpen(false)}
                className="rounded-xl bg-white text-primaryVar border border-primaryVar px-6 h-10 flex items-center justify-center font-medium transition-all"
              >
                بازگشت
              </button>
              <button
                onClick={() => {
                  setIsPreviewModalOpen(false);
                  handleSendMessage();
                }}
                className="rounded-xl bg-primaryVar text-white px-6 h-10 flex items-center justify-center font-medium hover:bg-primaryVar/80 transition-all disabled:bg-primaryVar/50"
                disabled={!message.trim()}
              >
                تایید و ارسال پیام
              </button>
            </>
          }
        />
      ) : (
        <ModalWithImageEmailBackground
          buttons={
            <div className="flex items-center justify-center gap-4 mt-4">
              <button
                onClick={() => setIsPreviewModalOpen(false)}
                className="rounded-xl bg-white text-primaryVar border border-primaryVar px-6 h-10 flex items-center justify-center font-medium transition-all"
              >
                بازگشت
              </button>
              <button
                onClick={() => {
                  setIsPreviewModalOpen(false);
                  handleSendMessage();
                }}
                className="rounded-xl bg-primaryVar text-white px-6 h-10 flex items-center justify-center font-medium hover:bg-primaryVar/80 transition-all disabled:bg-primaryVar/50"
                disabled={!emailTitle.trim() || !emailContent.trim()}
              >
                تایید و ارسال پیام
              </button>
            </div>
          }
          open={isPreviewModalOpen}
          onClose={() => setIsPreviewModalOpen(false)}
        >
          <div className="w-full h-full">
            <div className="absolute top-40 right-36 text-end font-semibold text-base">
              {emailTitle}
            </div>
            <div className="flex flex-col items-start mt-16 justify-center h-full mr-32">
              <div className="text-right text-base font-normal whitespace-pre-line mb-8 max-w-[480px] mt-16">
                {emailContent}
              </div>
            </div>
          </div>
        </ModalWithImageEmailBackground>
      )}
    </div>
  );
};